import { useEffect, useRef } from "react";
import { Portal, CloseButton, Box } from "@chakra-ui/react-v3";
import { Z_INDEX } from "../constants/z-index";
import { acquireScrollLock, releaseScrollLock } from "../utils/overlay-scroll-lock";

export type SidePanelDrawerProps = {
  open: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  children: React.ReactNode;
};

/**
 * Custom high-performance drawer component.
 * Uses CSS-only animations (GPU-accelerated transforms) to avoid forced reflow.
 * Replaces Chakra Drawer which caused 300-400ms of layout thrashing.
 */
export function SidePanelDrawer({ open, onOpenChange, children }: SidePanelDrawerProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    if (!open) return;

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onOpenChange({ open: false });
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onOpenChange]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) acquireScrollLock();
    return () => releaseScrollLock();
  }, [open]);

  if (!open) return null;

  return (
    <Portal>
      {/* Backdrop */}
      <Box
        position="fixed"
        inset={0}
        bg="blackAlpha.600"
        zIndex={Z_INDEX.SIDE_PANEL_BACKDROP}
        onClick={() => onOpenChange({ open: false })}
        style={{
          animation: "fadeIn 200ms ease-out",
        }}
      />

      {/* Drawer Content */}
      <Box
        ref={contentRef}
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        width="min(800px, 70vw)"
        bg="bg.panel"
        boxShadow="2xl"
        zIndex={Z_INDEX.SIDE_PANEL_CONTENT}
        overflowY="auto"
        role="dialog"
        aria-modal="true"
        aria-label="Side panel"
        tabIndex={-1}
        style={{
          animation: "slideInRight 200ms cubic-bezier(0.16, 1, 0.3, 1)",
          // Use transform for GPU acceleration - no forced reflow!
          willChange: "transform",
        }}
      >
        {/* Close button */}
        <Box position="absolute" top={6} right={6} zIndex={1}>
          <CloseButton
            aria-label="Close side panel"
            size="sm"
            onClick={() => onOpenChange({ open: false })}
          />
        </Box>

        {/* Content from side panel components */}
        {children}
      </Box>

      {/* CSS animations - GPU accelerated, no layout reads! */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes slideInRight {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
        `}
      </style>
    </Portal>
  );
}
